import { Suspense } from 'react'

import Loader from '@/components/Atoms/Loader/Loader'
import { useFeedLogic } from '@/hooks/search/useFeedLogic'
import { useScreenSize } from '@/hooks/ui/useScreenSize'
import FeedSection from '@/pages/Feed/FeedSection/FeedSection'
import { useFeedStore } from '@/stores/feed/feedStore'
import { useSearchStore } from '@/stores/feed/searchStore'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import { safeLazy } from '@/utils/app/safeLazy'
import getProductKey from '@/utils/products/getProductKey'

import cn from './Feed.module.scss'

const Filters = safeLazy(() => import('@/components/Filters/Filters'))
const ProductCard = safeLazy(
  () => import('@/pages/Feed/ProductCard/ProductCard')
)
const ProductsTable = safeLazy(
  () => import('@/components/Feed/ProductsTable/ProductsTable')
)

function Feed() {
  const {
    openFilters,
    setOpenFilters,
    dataFetchingError,
    loadMoreRef,
    hasMore,
  } = useFeedLogic()

  const screen = useScreenSize()
  const organisation = useUserInfoStore((s) => s.organisation)
  const { searchTerm } = useSearchStore((s) => s)

  const { products, productsLoading, columnsKeys, paginatedTableLoading } =
    useFeedStore((s) => s)

  const isInitialLoading = productsLoading
  const isLoadingMore = paginatedTableLoading && products.length > 0

  return (
    <>
      <Suspense fallback={null}>
        <Filters open={openFilters} setOpen={setOpenFilters} />
      </Suspense>

      <FeedSection
        setOpenFilters={setOpenFilters}
        dataFetchingError={dataFetchingError}
      />

      {[screen?.md, screen?.sm, screen?.xs].some(Boolean) && (
        <div className={cn.productsContainer}>
          {!isInitialLoading && (
            <div className={cn.productsCards}>
              {products.map((product, index) => (
                <Suspense
                  key={`${getProductKey(product)}-${index}`}
                  fallback={null}
                >
                  <ProductCard product={product} columnsKeys={columnsKeys} />
                </Suspense>
              ))}
            </div>
          )}

          {!isInitialLoading && hasMore && (
            <div ref={loadMoreRef} className={cn.infiniteScrollTrigger}>
              {isLoadingMore && <Loader />}
            </div>
          )}

          {!hasMore && products.length > 0 && (
            <div className={cn.endOfList}>Все товары загружены</div>
          )}

          {isInitialLoading && (
            <div className={cn.mobileLoaderOverlay}>
              <Loader />
            </div>
          )}
        </div>
      )}

      {[screen?.lg, screen?.xl].some(Boolean) && (
        <div>
          {!isInitialLoading && products.length > 0 && (
            <div className={cn.feedContent}>
              {searchTerm ? (
                <div className={cn.firstPageTables}>
                  <Suspense fallback={null}>
                    <ProductsTable products={[products[0]]} bestMatchTable />
                  </Suspense>
                  {products.length > 1 && (
                    <Suspense fallback={null}>
                      <ProductsTable
                        products={products.slice(1)}
                        showColumnFilters={false}
                      />
                    </Suspense>
                  )}
                </div>
              ) : (
                <Suspense fallback={null}>
                  <ProductsTable products={products} />
                </Suspense>
              )}

              {hasMore && (
                <div ref={loadMoreRef} className={cn.feedLoadMoreTrigger}>
                  {isLoadingMore && <Loader size={20} />}
                </div>
              )}

              {!hasMore && products.length > 0 && (
                <div className={cn.endOfListText}>Все товары загружены</div>
              )}
            </div>
          )}

          {isInitialLoading && <Loader paddingTop={organisation ? 72 : 120} />}
        </div>
      )}
    </>
  )
}

export default Feed
